import { forwardRef, useEffect, useRef } from 'react'
import { Check, Minus } from 'lucide-react'

/**
 * Yes/no and pick-several controls. Added 2026-10-18 from the patient-section
 * move: pages kept native checkboxes, a local Toggle and hand-coloured chip rows
 * because the kit had none of these.
 *
 *   <Checkbox>     one yes/no in a FORM (เคสแทรก, ยืนยันว่าตรวจแล้ว) — label right of the box
 *   <Switch>       an on/off SETTING that takes effect immediately (auto fee, แสดงที่ปิดแล้ว)
 *   <ChoiceChips>  pick one or several from a short list where each option has its OWN
 *                  colour (tooth-chart types, consent types, URI symptoms). For plain
 *                  one-of-a-few without colours, use <Segmented>.
 */

/** checked, onChange(bool), label, hint, disabled, indeterminate (the "some of them" dash), aria-label (when there is no visible label). */
export const Checkbox = forwardRef(function Checkbox({ checked = false, onChange, label, hint, disabled = false, indeterminate = false, id, 'aria-label': ariaLabel, className = '' }, ref) {
  const inner = useRef(null)
  useEffect(() => { if (inner.current) inner.current.indeterminate = !!indeterminate && !checked }, [indeterminate, checked])
  const setRef = (el) => { inner.current = el; if (typeof ref === 'function') ref(el); else if (ref) ref.current = el }
  const on = checked || indeterminate
  return (
    <label className={`inline-flex items-start gap-2.5 select-none ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${className}`}>
      <span className="relative flex-none mt-0.5">
        <input ref={setRef} id={id} type="checkbox" checked={checked} disabled={disabled} aria-label={ariaLabel}
          onChange={(e) => onChange?.(e.target.checked)} className="peer sr-only" />
        <span className={`w-[18px] h-[18px] rounded-[5px] border flex items-center justify-center transition-colors
          peer-focus-visible:ring-4 peer-focus-visible:ring-brand-100 ${on ? 'bg-brand-600 border-brand-600 text-white' : 'bg-white border-slate-300'}`}>
          {indeterminate && !checked ? <Minus size={12} strokeWidth={3} /> : checked ? <Check size={12} strokeWidth={3} /> : null}
        </span>
      </span>
      {(label || hint) && (
        <span className="min-w-0">
          {label && <span className="block text-[13.5px] text-slate-700 leading-snug">{label}</span>}
          {hint && <span className="block text-[12px] text-slate-400">{hint}</span>}
        </span>
      )}
    </label>
  )
})

/** checked, onChange(bool), label (left of the switch), hint, disabled, size 'md' | 'sm'. */
export function Switch({ checked = false, onChange, label, hint, disabled = false, size = 'md', className = '' }) {
  const sm = size === 'sm'
  return (
    <label className={`inline-flex items-center gap-2.5 select-none ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${className}`}>
      {(label || hint) && (
        <span className="min-w-0">
          {label && <span className="block text-[13.5px] text-slate-700 leading-snug">{label}</span>}
          {hint && <span className="block text-[12px] text-slate-400">{hint}</span>}
        </span>
      )}
      <button type="button" role="switch" aria-checked={checked} disabled={disabled} onClick={() => onChange?.(!checked)}
        className={`relative flex-none rounded-full transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-100
          ${sm ? 'w-8 h-[18px]' : 'w-10 h-[22px]'} ${checked ? 'bg-brand-600' : 'bg-slate-300'}`}>
        <span className={`absolute top-[2px] left-[2px] rounded-full bg-white shadow transition-transform
          ${sm ? 'w-[14px] h-[14px]' : 'w-[18px] h-[18px]'} ${checked ? (sm ? 'translate-x-[14px]' : 'translate-x-[18px]') : ''}`} />
      </button>
    </label>
  )
}

/**
 * options: [{ value, label, color?, count?, disabled? }] — `color` is any CSS colour
 *   (hex from the data, e.g. an appointment type's colour). No colour → brand.
 * Single: value / onChange(value). multiple: values (Set | array) / onChange(Set).
 * allowNone (single only): clicking the active chip clears it to ''.
 */
export function ChoiceChips({ options, value, values, multiple = false, onChange, allowNone = false, size = 'md', disabled = false, className = '' }) {
  const sel = multiple ? new Set([...(values instanceof Set ? values : values || [])].map(String)) : null
  const isOn = (o) => (multiple ? sel.has(String(o.value)) : String(o.value) === String(value ?? ''))
  const click = (o) => {
    if (disabled || o.disabled) return
    if (multiple) {
      const next = new Set(sel)
      const k = String(o.value)
      if (next.has(k)) next.delete(k); else next.add(k)
      onChange?.(new Set(options.filter((x) => next.has(String(x.value))).map((x) => x.value)))
    } else {
      onChange?.(isOn(o) && allowNone ? '' : o.value)
    }
  }
  const pad = size === 'sm' ? 'px-2 py-0.5 text-[12px]' : 'px-2.5 py-1 text-[13px]'
  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`} role={multiple ? 'group' : 'radiogroup'}>
      {options.map((o) => {
        const on = isOn(o)
        const c = o.color || '#2563eb'
        const style = on
          ? { backgroundColor: `color-mix(in srgb, ${c} 14%, white)`, color: `color-mix(in srgb, ${c} 75%, black)`, boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${c} 45%, white)` }
          : undefined
        return (
          <button key={String(o.value)} type="button" disabled={disabled || o.disabled}
            role={multiple ? 'checkbox' : 'radio'} aria-checked={on} onClick={() => click(o)} style={style}
            className={`inline-flex items-center gap-1.5 rounded-full font-medium whitespace-nowrap transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${pad}
              ${on ? '' : 'bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:ring-slate-300 hover:text-slate-900'}`}>
            {o.color && <span className="w-2 h-2 rounded-full flex-none" style={{ backgroundColor: c }} />}
            {o.label}
            {o.count != null && <span className="tabular-nums text-[11.5px] opacity-70">{o.count}</span>}
          </button>
        )
      })}
    </div>
  )
}
